import { IBookingState, IDelayedBooking, IConflictState } from './types';
import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  IBooking,
  FetchingStatus,
  IPaymentDetailsBase,
  servicePaymentType,
} from 'shared-types/SharedTypes';
import { DiaryRootState } from 'appRoot/rootReducer';
import BookingLookupService from 'external-services/booking-lookup-service';
import ServicesLookupService from 'external-services/services-lookup-service';
import { cloneDeep, sortBy } from 'lodash-es';
import { IServiceViewItem } from 'external-services/booking-lookup-service/types';
import { ISchedule, IDiarySchedule, bookingStatusType, IBookingFromPouch } from 'shared-types';
import ViewActionsService from 'app/actions/view.actions';
import { communicationActions } from 'booking-creator/reducers/communication/slice';
import { displayActions, exitSlide } from 'booking-creator/reducers/display/slice';
import { rootDisplayActions } from 'app/reducers/display/slice';
import { SlidingStatesEnum } from 'app/reducers/display/types';
import { BookingMode } from 'booking-creator/reducers/display/types';
import { setPaymentUrl } from 'booking-creator/reducers/payment/slice';
import PouchActions, { PouchDocType } from 'app/actions/pouch.actions';
import store, { AppDispatch } from 'appRoot/Store';
import PouchSyncService from 'external-services/pouch-sync-service';
import PaymentService from 'app/services/payment/payment.service';
import { tableDragActions } from 'floorplan-v2/app/reducers/tableDrag/slice';
import { IConflict } from 'external-services/bookings-conflicts-service/bookingConflicts.types';
import BookingNormaliseService from 'external-services/booking-normalise-service/bookingNormalise.service';
import { BookingAvailabilityService } from 'external-services/booking-availability-service';
import { isBookingCreator } from 'app/reducers/settings/slice';
import { checkDuplicateBooking } from './helper';
import BookingLockService from 'external-services/booking-lock-service';

const NS = 'bookingSlice';

const initialState: IBookingState = {
  couchReducedBookings: null, // @todo: this may need a better name, like visibleBookings
  status: FetchingStatus.idle,
  bookingIdsForCouchResponse: [],
  pouchListenerUnsubscribes: [],
  bookingConflicts: {
    timeConflicts: [],
    tableConflicts: [],
    overlapConflicts: [],
  },
};

async function getFilterBookings(
  bookings: IBooking[],
  selectedSchedule: IDiarySchedule
): Promise<IBooking[]> {
  if (!bookings.length) {
    return Promise.resolve([]);
  }

  const haveAtleastOneFunctionTrackerBooking =
    bookings?.some((booking) => booking.functionTracker === true) || false;
  const serviceViewList = await ServicesLookupService.getServicesAndServiceGroupsAsync(
    selectedSchedule
  );
  const bookingsByService = BookingLookupService.setBookingsByServiceObject(
    serviceViewList as any as IServiceViewItem[],
    haveAtleastOneFunctionTrackerBooking
  );

  const { filteredBookings } = BookingLookupService.filterBookings(
    0,
    selectedSchedule as any as ISchedule,
    bookings,
    bookingsByService,
    serviceViewList as any as IServiceViewItem[],
    null,
    true
  );
  return sortBy(filteredBookings, ['time', 'firstName']);
}

export const editInBookingCreator = createAsyncThunk(
  'booking/editInBookingCreator',
  async (payload: { activeBooking: IBooking }, { dispatch, getState }): Promise<void> => {
    const activeBooking: IBooking = cloneDeep(payload.activeBooking);

    // recurring bookings won't have paymentDetails on them, so we must add it here
    if (!activeBooking.paymentDetails) {
      // when first loading a booking from widget, the paymentDetails will be undefined, so we must set them here
      const paymentDetails =
        PaymentService.getPaymentDetailsFromWidgetBooking(activeBooking) ||
        ({
          paymentType: servicePaymentType.noPayment,
        } as IPaymentDetailsBase);
      activeBooking.paymentDetails = paymentDetails as IPaymentDetailsBase;
    }

    // Note: these should be called from src/app/floorplan-v2/FloatingBookingCard/index.tsx -> handleBookingClicked as well
    dispatch(displayActions.setIsManualPax());
    dispatch(communicationActions.setSmsEnabled(false));
    dispatch(communicationActions.setEmailEnabled(false));
    dispatch(displayActions.setBackUpActiveBooking(cloneDeep(activeBooking)));
    dispatch(displayActions.setActiveBooking(activeBooking));
    dispatch(rootDisplayActions.setSlideState(SlidingStatesEnum.BCSlidingIn));
    dispatch(displayActions.setMode(BookingMode.viewBooking));
    dispatch(setPaymentUrl(activeBooking));
    dispatch(displayActions.setIsManualCopyOriginalBooking(false));
  }
);

export const loadBookingsFromCurrentSchedule = createAsyncThunk(
  'booking/loadBookingsFromCurrentSchedule',
  async (payload, { dispatch, getState }): Promise<IBooking[]> => {
    try {
      const { scheduleReducer } = getState() as DiaryRootState;

      const { selectedSchedule } = scheduleReducer;

      // @todo: Pass date to get different schedule, for ticket UI-114 -- change date state, call loadBookingsFromCurrentSchedule() again

      // selectedSchedule.services[0].startTime.setHours(8, 0, 0, 0);
      const { bookings } = await BookingLookupService.getBySchedule(selectedSchedule);
      const filteredBookings = await getFilterBookings(bookings, selectedSchedule);
      return filteredBookings;
    } catch (e) {
      console.log(NS, 'loadBooking error', e);
    }
    return [];
  }
);

export const updateVisibleBookings = createAsyncThunk(
  'booking/updateVisibleBookings',
  async (
    payload: { updatedBookingsList: IBooking[]; recentChangeBookings: IBooking[] },
    { dispatch, getState }
  ): Promise<{ filteredBookings: IBooking[]; recentChangeBookings: IBooking[] }> => {
    try {
      const { scheduleReducer } = getState() as DiaryRootState;
      const filteredBookings = await getFilterBookings(
        payload.updatedBookingsList,
        scheduleReducer.selectedSchedule
      );

      const recentChangeBookings = payload.recentChangeBookings;

      return { filteredBookings, recentChangeBookings };
    } catch (e) {
      console.log(NS, 'loadBooking error', e);
    }
    return { filteredBookings: [], recentChangeBookings: [] };
  }
);

export const updateDelayedBookings = createAsyncThunk(
  'booking/updateDelayedBookings',
  async (
    payload: { delayedBooking: Partial<IBooking>; changedProperty: keyof Partial<IBooking> },
    { dispatch, getState }
  ): Promise<{ delayedBookings: IDelayedBooking[] }> => {
    const { bookingReducer } = getState() as DiaryRootState;
    const oldDelayedBookings = bookingReducer.delayedBookings || [];
    const delayedBookings = oldDelayedBookings.concat({
      booking: payload.delayedBooking,
      changedProperty: payload.changedProperty,
    });
    return { delayedBookings };
  }
);
export const updateBookingIdsForCouchResponse = createAsyncThunk(
  'booking/updateBookingIdsForCouchResponse',
  async (payload: string, { dispatch, getState }): Promise<string[]> => {
    const { bookingReducer } = getState() as DiaryRootState;
    const bookingIdsForCouchResponse = bookingReducer.bookingIdsForCouchResponse;
    if (!bookingIdsForCouchResponse.length) {
      // no need do anything if bookingIdsForCouchResponse is empty
      return [];
    }
    const newBookingIdsForCouchResponse = bookingIdsForCouchResponse.filter((id) => id !== payload);
    if (!newBookingIdsForCouchResponse.length) {
      ViewActionsService.onCloseDragBookingConfirmation();
      // new modal works
      dispatch(tableDragActions.setIsDragSaveActionComplete(true));
    }
    return newBookingIdsForCouchResponse;
  }
);

const viewBooking = (booking: IBooking, dispatch: AppDispatch): string => {

  const { filterBookingReducer, bookingReducer } = store.getState() as DiaryRootState;
  const { bookingsByType } = filterBookingReducer;
  const { couchReducedBookings } = bookingReducer;

  const activeBookingFromStore = couchReducedBookings?.find((b) => b._id === booking._id);

  if (!activeBookingFromStore) {
    console.warn(NS, 'viewBooking: booking not found in store', booking._id);
    return 'Possible delay syncing booking. Please try again.';
  }

  const isPaymentLocked = BookingLockService.checkLockedPendingPayment(activeBookingFromStore);
  if (isPaymentLocked.isLocked) {
    return isPaymentLocked.reason;
  }

  const activeBooking = BookingNormaliseService.normalizeModelToView(
    cloneDeep(activeBookingFromStore) as unknown as IBookingFromPouch
  );

  // don't use PaymentService.conditionallyShowLockedBookingDialog because it replaces the toast when locked

  if (isBookingCreator()) {
    BookingLookupService.getById(activeBooking._id, activeBooking.time).then((booking) => {
      /**
       * There is a property called 'tablesAvaiable' that gets added to WL bookings on bookingsByType, inside the
       * BookingListFilterService, but it does not get saved to the booking. So here we need look up the value and
       * attach it to the active booking again when opening it. It is responsible for displaying the "Seat Now"
       * button on the WaitList booking summary.
       */
      BookingAvailabilityService.addTablesAvailableFlagToCleanBooking(
        booking,
        bookingsByType?.waitlist
      );

      const {rootDisplayReducer} = store.getState() as DiaryRootState;
      const slideState = rootDisplayReducer.slideState;
      if (slideState !== SlidingStatesEnum.SlideOutComplete) {
        dispatch(exitSlide({ isCloseBtn: false, slideOutCompleteCB: () => {
          dispatch(editInBookingCreator({ activeBooking: booking }));
        } }));
      } else {
        dispatch(editInBookingCreator({ activeBooking: booking }));
      }
    });
  } else {
    ViewActionsService.onEditBooking({
      booking: activeBooking,
    });
  }

  return '';
};


export const syncCouchBookingUpdates = createAsyncThunk(
  'booking/syncCouchBookingUpdates',
  async (payload: void, { dispatch, getState }): Promise<(() => void)[]> => {
    const unsubscribeFunctions: (() => void)[] = [];

    PouchActions.registerForReadyCB(() => {
      // NBI-8701: cleanup pouch listeners when not on bookings page - was creating a memory leak
      const unsubscribeDoctype = PouchActions.handlePouchSyncedDoctype(
        PouchDocType.booking,
        async (evt, bookingIds: string[]) => {
          if (bookingIds.length > 1) {
            console.warn('updateOrInjectDocumentFromPouchUpdate', bookingIds);
          }

          /**
           * useSelector won't work in here because the PouchActions.handlePouchSyncedDoctype isn't part of redux, so need to
           * call store.getState
           */
          const _couchReducedBookings = store.getState().bookingReducer.couchReducedBookings;
          if (!_couchReducedBookings) {
            // if still loading bookings, no need to update anything
            return;
          }
          const selectedDate = store.getState().scheduleReducer.selectedDate;

          for (const id of bookingIds) {
            let updates: {
              updatedBookingsList: IBooking[];
              recentChangeBookings: IBooking[];
              isNewSameDayBooking?: boolean;
            };
            try {
              updates = await PouchSyncService.updateOrInjectDocumentFromPouchUpdate({
                existingBookings: _couchReducedBookings,
                documentIds: [id],
                selectedDate,
                actionValueCallback: () => {
                  // not needed yet for Diary
                },
                blockoutsCallback: () => {
                  // not needed yet for Diary
                },
              });
            } catch (err) {
              console.warn(
                NS,
                "update failed for 'PouchSyncService.updateOrInjectDocumentFromPouchUpdate'",
                bookingIds,
                selectedDate,
                err
              );
              // @todo: handle error with some UI
              continue;
            }

            if (updates) {
              await dispatch(updateVisibleBookings(updates));
              if (updates.isNewSameDayBooking) {
                const newBooking = updates.recentChangeBookings[0];
                checkDuplicateBooking(newBooking, updates.updatedBookingsList, dispatch, () => {
                  const { rootDisplayReducer } = store.getState() as DiaryRootState;
                  const toastCleanPayload = cloneDeep(rootDisplayReducer.toastCleanPayload);

                  const warningText = viewBooking(newBooking, dispatch as AppDispatch);
                  toastCleanPayload.warningText = warningText;
                  dispatch(rootDisplayActions.showToastClean(toastCleanPayload));
                });
              }
            }
            dispatch(actions.clearDelayedBookings());
          }
        }
      );
      unsubscribeFunctions.push(unsubscribeDoctype);
    });

    return unsubscribeFunctions;
  }
);

const bookingSlice = createSlice({
  name: 'booking', // used in action types
  initialState,
  reducers: {
    clearRecentChangeBookingIds: (state, action: PayloadAction<void>) => {
      state.recentChangeBookings = null;
      state.recentChangeBookingIdToIgnore = '';
    },
    setRecentChangeBookingIdToIgnore: (state, action: PayloadAction<string>) => {
      state.recentChangeBookingIdToIgnore = action.payload;
    },
    setBookingConflicts: (state, action: PayloadAction<IConflictState>) => {
      state.bookingConflicts = action.payload;
    },
    setOverlapConflicts: (state, action: PayloadAction<IConflict[]>) => {
      state.bookingConflicts.overlapConflicts = action.payload;
    },
    clearDelayedBookings: (state, action: PayloadAction<void>) => {
      state.delayedBookings = [];
    },
    setBookingIdsForCouchResponse: (state, action: PayloadAction<string[]>) => {
      state.bookingIdsForCouchResponse = action.payload;
    },
    cleanupPouchListeners: (state, action: PayloadAction<void>) => {
      // Call all unsubscribe functions
      if (state.pouchListenerUnsubscribes) {
        state.pouchListenerUnsubscribes.forEach((unsubscribe) => {
          try {
            unsubscribe();
          } catch (error) {
            console.warn(NS, 'Error unsubscribing pouch listener:', error);
          }
        });
        state.pouchListenerUnsubscribes = [];
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(updateVisibleBookings.pending, (state, action) => {
        state.status = FetchingStatus.loading;
      })
      .addCase(loadBookingsFromCurrentSchedule.pending, (state, action) => {
        state.status = FetchingStatus.loading;
      })
      .addCase(loadBookingsFromCurrentSchedule.fulfilled, (state: IBookingState, action) => {
        state.couchReducedBookings = action.payload;
        state.status = FetchingStatus.succeeded;
      })
      .addCase(updateVisibleBookings.fulfilled, (state: IBookingState, action) => {
        state.couchReducedBookings = action.payload.filteredBookings;
        state.recentChangeBookings = action.payload.recentChangeBookings;
        state.status = FetchingStatus.succeeded;
      })
      .addCase(updateBookingIdsForCouchResponse.fulfilled, (state: IBookingState, action) => {
        state.bookingIdsForCouchResponse = action.payload;
      })
      .addCase(updateDelayedBookings.fulfilled, (state: IBookingState, action) => {
        state.delayedBookings = action.payload.delayedBookings;
      })
      .addCase(syncCouchBookingUpdates.fulfilled, (state: IBookingState, action) => {
        state.pouchListenerUnsubscribes = action.payload;
      });
  },
});


// Extract the action creators object and the reducer
const { actions, reducer } = bookingSlice;
// Extract and export each action creator by name
export { actions as bookingActions };
// Export the reducer, either as a default or named export
export default reducer;
